import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, query, where, orderBy } from 'firebase/firestore';
import { db, handleFirestoreError, OperationType } from '../lib/firebase';
import { SurveyLink } from '../types';
import { ExternalLink, ClipboardList } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

export default function SurveyList() {
  const [surveys, setSurveys] = useState<SurveyLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'active' | 'history'>('active');

  useEffect(() => {
    // Only fetch active surveys for the end-user
    const q = query(
      collection(db, 'surveys'),
      where('status', '==', 'active'),
      orderBy('createdAt', 'desc')
    );
    
    const unsub = onSnapshot(q, (snap) => {
      setSurveys(snap.docs.map(d => ({ id: d.id, ...d.data() } as SurveyLink)));
      setLoading(false);
    }, (error) => {
      // Create index if needed
      if (error.message && error.message.includes('requires an index')) {
        console.warn('Firestore index needed for surveys:', error.message);
        // Fallback: fetch without complex ordering if index missing
        const fallbackQ = query(collection(db, 'surveys'), where('status', '==', 'active'));
        const fallbackUnsub = onSnapshot(fallbackQ, (fallbackSnap) => {
          const fetched = fallbackSnap.docs.map(d => ({ id: d.id, ...d.data() } as SurveyLink));
          // Manual sort fallback
          fetched.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
          setSurveys(fetched);
          setLoading(false);
        });
        return () => fallbackUnsub();
      } else {
        handleFirestoreError(error, OperationType.LIST, 'surveys');
        setLoading(false);
      }
    });
    
    return () => unsub();
  }, []);

  if (loading) {
    return <div className="text-center p-8 text-lcars-blue uppercase tracking-widest animate-pulse font-bold">Accessing Main Computer...</div>;
  }

  const now = new Date();
  
  const visibleSurveys = surveys.filter(survey => {
    // If it has an openDate and we are before it, hide it completely
    if (survey.openDate && new Date(survey.openDate) > now) {
      return false;
    }
    
    const isClosed = survey.closeDate ? new Date(survey.closeDate) < now : false;
    
    return activeTab === 'active' ? !isClosed : isClosed;
  });

  return (
    <div className="space-y-6">
      <div className="bg-lcars-blue text-black p-4 rounded-xl flex items-center gap-4">
        <ClipboardList className="w-8 h-8" />
        <div>
          <h2 className="text-xl font-bold uppercase tracking-widest">Surveys & Forms</h2>
          <p className="text-sm opacity-80">Official forms and feedback requests authorized by Command</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'active' | 'history')} className="space-y-6">
        <TabsList className="bg-[var(--background)] border border-white/20 h-auto p-1 flex">
          <TabsTrigger 
            value="active" 
            className="flex-1 lcars-pill data-[state=active]:bg-lcars-blue data-[state=active]:text-black text-[var(--foreground)]/60"
          >
            ACTIVE FORMS
          </TabsTrigger>
          <TabsTrigger 
            value="history" 
            className="flex-1 lcars-pill data-[state=active]:bg-lcars-gold data-[state=active]:text-black text-[var(--foreground)]/60"
          >
            SURVEY HISTORY
          </TabsTrigger>
        </TabsList>

        <TabsContent value="active" className="space-y-4 outline-none">
          {visibleSurveys.length === 0 ? (
            <div className="text-center p-12 bg-[var(--background)] border border-white/10 rounded-xl">
              <p className="text-[var(--foreground)]/40 uppercase tracking-widest font-bold">No active surveys at this time.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {visibleSurveys.map(survey => renderSurveyCard(survey, true))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="history" className="space-y-4 outline-none">
          {visibleSurveys.length === 0 ? (
            <div className="text-center p-12 bg-[var(--background)] border border-white/10 rounded-xl">
              <p className="text-[var(--foreground)]/40 uppercase tracking-widest font-bold">No past surveys found.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {visibleSurveys.map(survey => renderSurveyCard(survey, false))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );

  function renderSurveyCard(survey: SurveyLink, isActive: boolean) {
    return (
      <div key={survey.id} className={`group bg-[var(--background)] rounded-xl overflow-hidden flex flex-col h-full border ${isActive ? 'border-lcars-blue/30 hover:border-lcars-blue' : 'border-white/10 opacity-70'}`}>
        <div className={`p-4 border-b ${isActive ? 'bg-lcars-blue/10 border-lcars-blue/20' : 'bg-white/5 border-white/10'}`}>
          <h3 className="text-lg font-bold text-lcars-gold truncate">{survey.title}</h3>
        </div>
        
        {(survey.openDate || survey.closeDate) && (
          <div className="px-4 pt-3 flex flex-wrap gap-4 text-xs font-mono text-lcars-blue/70">
            {survey.openDate && <span>Opens: {new Date(survey.openDate).toLocaleDateString()}</span>}
            {survey.closeDate && <span className={!isActive ? 'text-lcars-red' : ''}>Closes: {new Date(survey.closeDate).toLocaleDateString()}</span>}
          </div>
        )}
        
        <div className="p-4 flex-grow">
          <p className="text-sm text-[var(--foreground)]/80 whitespace-pre-wrap">{survey.description}</p>
        </div>
        
        <div className="p-4 pt-0 mt-auto">
          <a 
            href={survey.url} 
            target="_blank" 
            rel="noopener noreferrer"
            className={`w-full flex items-center justify-center gap-2 font-bold uppercase tracking-widest py-3 rounded-lg transition-colors ${isActive ? 'bg-lcars-blue text-black hover:bg-white' : 'bg-white/10 text-[var(--foreground)]/50 hover:bg-white/20 hover:text-[var(--foreground)]'}`}
          >
            <ExternalLink className="w-4 h-4" /> {isActive ? 'Open Secure Link' : 'View Closed Form'}
          </a>
        </div>
      </div>
    );
  }
}
